import React from 'react';
import { Box, IconButton, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import {
  AddCircleOutline,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
} from '@mui/icons-material';
import { sheetTone } from './sheetStyles';

/**
 * The banded section header from the source spreadsheet.
 *
 * `tone="supervisor"` switches to the accent used for the parts a supervisor
 * fills in. Both tints come from the app theme — see `sheetStyles`.
 */
export const SectionBand = ({ title, note, tone = 'employee' }) => {
  const colors = sheetTone(useTheme(), tone);

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        backgroundColor: colors.band,
        border: '1px solid',
        borderColor: colors.border,
        borderBottom: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {note && (
        <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
};

/** A banded free-text section that commits on blur. */
const ProseSection = ({ title, note, tone, value, disabled, onCommit, placeholder }) => {
  const [draft, setDraft] = React.useState(value || '');

  // Re-sync when the server hands back a different value (e.g. after a pull-in).
  React.useEffect(() => setDraft(value || ''), [value]);

  return (
    <Box sx={{ mb: 2 }}>
      <SectionBand title={title} note={note} tone={tone} />
      <TextField
        fullWidth
        multiline
        minRows={2}
        disabled={disabled}
        value={draft}
        placeholder={disabled ? '' : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== (value || '')) onCommit(draft);
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
      />
    </Box>
  );
};

/**
 * Accomplishments are a bulleted list only, so they are edited as discrete rows
 * rather than free text — that is what the report stores and what the PDF
 * renders as a `<ul>`.
 */
const BulletListSection = ({ title, items, disabled, onCommit }) => {
  const [draft, setDraft] = React.useState(items || []);

  React.useEffect(() => setDraft(items || []), [items]);

  const commit = (next) => {
    setDraft(next);
    onCommit(next.filter((item) => item.trim()));
  };

  const move = (index, delta) => {
    const next = [...draft];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    commit(next);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <SectionBand title={title} note="Bulleted list" />
      <Box sx={{ border: '1px solid', borderColor: 'divider', p: 1 }}>
        {draft.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
            Nothing listed yet.
          </Typography>
        )}
        {draft.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography sx={{ px: 0.5 }}>•</Typography>
            <TextField
              fullWidth
              size="small"
              disabled={disabled}
              value={item}
              onChange={(e) => {
                const next = [...draft];
                next[index] = e.target.value;
                setDraft(next);
              }}
              onBlur={() => {
                if (draft[index] !== (items || [])[index]) commit(draft);
              }}
            />
            {!disabled && (
              <>
                <Tooltip title="Move up">
                  <span>
                    <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                      <ArrowUpward fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={index === draft.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownward fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    onClick={() => commit(draft.filter((_, i) => i !== index))}
                  >
                    <DeleteOutline fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        ))}
        {!disabled && (
          <IconButton size="small" onClick={() => setDraft([...draft, ''])} sx={{ mt: 0.5 }}>
            <AddCircleOutline fontSize="small" />
            <Typography variant="body2" sx={{ ml: 0.5 }}>
              Add
            </Typography>
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

/**
 * The five narrative bands of the biweekly sheet, in sheet order. Which side is
 * editable comes from the server's `permissions` block rather than being
 * re-derived here.
 */
const NarrativeSections = ({ report, permissions, onChange }) => {
  const employeeLocked = !permissions?.can_edit_employee_fields;
  const supervisorLocked = !permissions?.can_edit_supervisor_fields;

  return (
    <Box>
      <ProseSection
        title="Priorities"
        value={report.priorities}
        disabled={employeeLocked}
        placeholder="What this period was meant to be about"
        onCommit={(value) => onChange({ priorities: value })}
      />
      <BulletListSection
        title="Accomplishments"
        items={report.accomplishments}
        disabled={employeeLocked}
        onCommit={(value) => onChange({ accomplishments: value })}
      />
      <ProseSection
        title="Issues / Roadblocks"
        value={report.issues_roadblocks}
        disabled={employeeLocked}
        placeholder="What got in the way"
        onCommit={(value) => onChange({ issues_roadblocks: value })}
      />
      <ProseSection
        title="Review & Reflection"
        value={report.review_reflection}
        disabled={employeeLocked}
        placeholder="Learnings and development goals"
        onCommit={(value) => onChange({ review_reflection: value })}
      />
      <ProseSection
        title="Supervisor Comments"
        note="To be filled by supervisor"
        tone="supervisor"
        value={report.supervisor_comments}
        disabled={supervisorLocked}
        onCommit={(value) => onChange({ supervisor_comments: value })}
      />
    </Box>
  );
};

export default NarrativeSections;
